import type { FunctionComponent } from 'react';
import { Box, Stack, Text } from '@mantine/core';

import { useTranslation } from 'i18n';
import { MARKETPLACE_CROP_CONFIG } from './marketplaceConfig';
import type { CropState } from './EditPhotoContent';
import { CroppedImagePreview } from './CroppedImagePreview';

const NS = 'menuPhotosPhotoDrawer';

type MarketplacePreviewCardsProps = {
  imageUrl: string;
  cropState: CropState;
  activeIntegrationNames: Set<string>;
};

export const MarketplacePreviewCards: FunctionComponent<MarketplacePreviewCardsProps> = ({
  imageUrl,
  cropState,
  activeIntegrationNames,
}) => {
  const { t } = useTranslation(NS);

  const chownowLabel = activeIntegrationNames.has('grubhub')
    ? t('MarketplacePreviewCards.chownowGrubhub')
    : t('MarketplacePreviewCards.chownow');

  const cards = [
    {
      id: 'chownow',
      name: chownowLabel,
      cropLabel: t('MarketplacePreviewCards.crop43'),
      aspectRatio: '4/3',
    },
    ...MARKETPLACE_CROP_CONFIG.filter(({ integrationName }) =>
      activeIntegrationNames.has(integrationName),
    ).map(({ integrationName, nameKey, cropLabelKey, cssRatio }) => ({
      id: integrationName,
      name: t(nameKey),
      cropLabel: t(cropLabelKey),
      aspectRatio: cssRatio,
    })),
  ];

  return (
    <Stack gap="sm">
      {cards.map(({ id, name, cropLabel, aspectRatio }) => (
        <Box
          key={id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--mantine-spacing-sm)',
            padding: '8px 24px 8px 8px',
            border: '1px solid var(--border-secondary)',
            borderRadius: 'var(--mantine-radius-sm)',
          }}
        >
          <Box style={{ width: 160, flexShrink: 0 }}>
            <CroppedImagePreview
              imageUrl={imageUrl}
              cropState={cropState}
              aspectRatio={aspectRatio}
            />
          </Box>
          <Stack gap={4}>
            <Text variant="label-sm">{name}</Text>
            <Text variant="sublabel-sm">{cropLabel}</Text>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};
